import type { PayloadAction } from "@reduxjs/toolkit";
import { createAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { message } from "antd";
import type { AxiosError } from "axios";
import axiosInstance from "../api/axiosInstance";
import { getUserInfo, handleChangePassword } from "../service/auth.service";
import type { UserRole } from "../service/members.service";

type User = {
  email: string;
  password: string;
};

type NewUser = User & {
  name: string;
  invitation?: string;
  coupon_code?: string;
  group_invite?: string;
};

type UserBasicInfo = {
  id: string;
  name: string;
  email: string;
};

export enum UserTier {
  FREE = 0,
  PRO = 1,
  GROUP = 2,
}

export type UserInfoProps = {
  total_recordings: number;
  free_recordings_limit: number;
  is_paying: boolean;
  plan: number;
  tier: UserTier;
  group_name: string;
  is_group_admin: boolean;
  role?: UserRole;
  is_tutorial_completed?: boolean;
  can_view_transcript?: boolean;
  record_first?: boolean;
  created_at?: string;
  free_trial_days_limit: number;
  enable_analytics: boolean;
  group_logo: string | null;
  onboarding_data?: {
    profession?: string;
    practice_size?: number;
    referral_source?: string;
    completed?: boolean;
    start_time?: string;
    reward_claimed?: boolean;
    timezone?: string;
    ehr?: string;
    work_as?: string;
  };
  practice_area: number;
  specialty_code?: string;
  specialty_label?: string;
  is_member_management_enabled?: boolean;
  is_ai_admin_worker_enabled?: boolean;
  is_compliance_enabled?: boolean;
  show_compliance_dashboard?: boolean;
  group_rpa_enabled?: boolean;
  // Superdoc memory is on for this user (group flag or Constance ramp list);
  // server-computed, drives the Memory nav item.
  memory_enabled?: boolean;
  enable_ehr_transfer?: boolean;
  // Server-computed on bootstrap: this user may hand-trigger an EHR patient pull.
  // The only signal that shows the Sync button — never re-derived client-side.
  can_sync_ehr_patients?: boolean;
  // Server-computed on bootstrap: the coverage card renders under order cards
  // for this user's practice (Constance COVERAGE_CARD_BY_GROUP). The order
  // card itself never changes with it.
  coverage_card_enabled?: boolean;
  // Server-computed on bootstrap: this user has an EHR day sheet to show — their own
  // pull, or an active auto-share from a clinician who has one (an MA on a shared EHR
  // login). The only signal that offers the "Today's schedule" sort.
  can_view_ehr_schedule?: boolean;
  is_superdoc_enabled?: boolean;
  // Superdoc-first flow: the account has it (Superdoc writes and codes the
  // note in the patient's chat) …
  is_superdoc_orchestrator?: boolean;
  // … and the web app shows it (chat-first layout, recordings marked for it).
  is_superdoc_orchestrator_web?: boolean;
  // A pulled EHR day sheet reaches this user (their own pull stamped one, or a clinician's
  // is auto-shared to them): Quick Start's card and recording card, else the classic ones.
  has_ehr_schedule?: boolean;
  // Demo mode (backend demo_ehr): this account's clinic runs on fixture data, not a
  // real EHR. Offers "Reset demo" in the header menu. Distinct from the logged-out
  // marketing page's appState.isDemoPath.
  is_demo_ehr?: boolean;
  group_payment_method?: "credit_card" | "invoice" | null;
  group_has_subscription?: boolean;
  group_billing_active?: boolean;
  // Advisory: the payer's subscription needs attention (declined card, bank
  // verification, overdue invoice). Drives the banner. NOT a lockout signal —
  // most of what opens this window leaves access fully intact.
  billing_payment_failing?: boolean;
  billing_payment_failing_reason?: "payment_failed" | "payment_action_required" | "invoice_overdue" | null;
  // Authoritative: access has actually been withdrawn for non-payment. This
  // is what the lockout takeover keys on.
  billing_suspended?: boolean;
  // Present only during an admin "login as user" (GOD mode) session —
  // server-truth companion to the local impersonation flag.
  impersonation?: {
    active: boolean;
    admin_email: string;
    user_email?: string;
    expires_at: string;
  };
};

type AuthApiState = {
  basicUserInfo?: UserBasicInfo | null;
  status: "idle" | "loading" | "failed";
  error: string | null;
  userInfo: UserInfoProps;
};

const initialState: AuthApiState = {
  basicUserInfo: localStorage.getItem("userInfo") ? JSON.parse(localStorage.getItem("userInfo") as string) : null,
  status: "idle",
  error: null,
  userInfo: {
    total_recordings: -1,
    free_recordings_limit: 0,
    is_paying: false,
    plan: 0,
    tier: 0,
    group_name: "",
    is_group_admin: false,
    free_trial_days_limit: 0,
    enable_analytics: false,
    group_logo: null,
    practice_area: 0,
    can_view_transcript: true,
    record_first: false,
    enable_ehr_transfer: false,
    is_superdoc_enabled: false,
    has_ehr_schedule: false,
  },
};

export const login = createAsyncThunk("login", async (data: User) => {
  const loginData = {
    username: data.email,
    password: data.password,
    email: data.email,
  };
  const response = await axiosInstance.post("/rest-auth/login/", loginData);
  const resData = response.data;

  localStorage.setItem("token", resData.key);

  return resData;
});

export const register = createAsyncThunk("register", async (data: NewUser) => {
  const dataTest = {
    first_name: data.name,
    email: data.email,
    password1: data.password,
    password2: data.password,
    username: data.email,
    invitation: data.invitation,
    coupon_code: data.coupon_code,
    group_invite: data.group_invite,
  };

  try {
    const response = await axiosInstance.post("/rest-auth/registration/", JSON.stringify(dataTest));
    const resData = response.data;

    localStorage.setItem("token", resData.key);

    return resData;
  } catch (error: unknown) {
    console.error(error);
    const axiosError = error as AxiosError<{ username?: string[]; detail?: string }>;

    if (axiosError.response?.data?.username?.[0]?.includes("A user with that username already exists.")) {
      message.error("The email is already in use. Please try a different email.");
    } else if (axiosError.response?.data?.detail) {
      message.error(axiosError.response?.data?.detail);
    } else {
      message.error("Registration failed. Please try again later.");
    }
  }
});

export const logout = createAsyncThunk("logout", async () => {
  // TODO: This endpoint does not exist
  const response = await axiosInstance.post("/logout", {});
  const resData = response.data;
  localStorage.removeItem("userInfo");
  return resData;
});

export const getUser = createAsyncThunk("users/profile", async (userId: string) => {
  const response = await axiosInstance.get(`/users/${userId}`);
  return response.data;
});

export const updateUserInfo = createAsyncThunk("auth/updateUserInfo", async () => {
  const response = await getUserInfo();
  return response.data;
});

export const changePassword = createAsyncThunk(
  "password/change",
  async (data: { email: string; newPassword: string }) => {
    const passwordResetData = {
      new_password1: data.newPassword,
      new_password2: data.newPassword,
    };
    const response = await handleChangePassword(passwordResetData);
    const resData = response.data;
    return resData;
  },
);

// Create an action to reset basicUserInfo
export const resetBasicUserInfo = createAction("auth/resetBasicUserInfo");

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    resetBasicUserInfo: (state) => {
      state.basicUserInfo = null;
      state.userInfo = {
        total_recordings: -1, // setting this to -1 because Intercom should open when there are 0 notes and the default notes is 0, so it's triggering the widget even before the API returns the response
        free_recordings_limit: 0,
        is_paying: false,
        plan: 0,
        tier: 0,
        group_name: "",
        is_group_admin: false,
        free_trial_days_limit: 0,
        enable_analytics: false,
        group_logo: null,
        practice_area: 0,
        can_view_transcript: true,
        is_ai_admin_worker_enabled: false,
        enable_ehr_transfer: false,
      };
    },
    setUserInfo: (state, action: PayloadAction<UserInfoProps>) => {
      state.userInfo = action.payload;
    },
    // Mirror of the timezone App.tsx just PATCHed to the server, so readers of the stored zone
    // (the schedule sort's clinic zone) see this load's value, not the one fetched a moment ago.
    setStoredTimezone: (state, action: PayloadAction<string>) => {
      state.userInfo.onboarding_data = { ...(state.userInfo.onboarding_data || {}), timezone: action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<UserBasicInfo>) => {
        state.status = "idle";
        state.basicUserInfo = action.payload;
      })
      .addCase(login.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Login failed";
      })

      .addCase(register.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action: PayloadAction<UserBasicInfo>) => {
        state.status = "idle";
        state.basicUserInfo = action.payload;
      })
      .addCase(register.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Registration failed";
      })

      .addCase(logout.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.status = "idle";
        state.basicUserInfo = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Logout failed";
      })

      .addCase(getUser.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getUser.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(getUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Get user profile data failed";
      })

      .addCase(changePassword.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Change password failed";
      })
      .addCase(updateUserInfo.fulfilled, (state, action) => {
        state.userInfo = action.payload;
      })
      .addCase(updateUserInfo.rejected, (state, action) => {
        state.error = action.error.message || "Failed to update user info";
        console.error("Failed to update user info:", action.payload);
      });
  },
});

export const { setUserInfo, setStoredTimezone } = authSlice.actions;

export default authSlice.reducer;
